import { DRXError, isDRXError } from '@/lib/drx-error';
import type { JobUseCase } from '../usecases/job.usecase';
import type {
  CreateJobData,
  GetJobData,
  UpdateJobData,
  JobSearchFilters,
  CreateJobApplicationData,
  GetJobApplicationData,
  JobApplicationStatus,
  JobStatistics,
} from '../repository/interfaces/job.repository.interface';

export class JobsService {
  constructor(private jobUseCase: JobUseCase) {}

  // ===== Job CRUD =====

  async createJob(creatorId: string, jobData: CreateJobData): Promise<GetJobData> {
    try {
      return await this.jobUseCase.createJob(creatorId, jobData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.createJob failed unexpectedly',
        userMessage: 'Could not create your job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJob(jobId: string): Promise<GetJobData | null> {
    try {
      return await this.jobUseCase.getJob(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.getJob failed unexpectedly',
        userMessage: 'Could not load the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobsByCreator(creatorId: string, status?: string): Promise<GetJobData[]> {
    try {
      return await this.jobUseCase.getJobsByCreator(creatorId, status);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.getJobsByCreator failed unexpectedly',
        userMessage: 'Could not load your job postings. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async searchJobs(filters: JobSearchFilters, limitCount?: number): Promise<GetJobData[]> {
    try {
      return await this.jobUseCase.searchJobs(filters, limitCount);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.searchJobs failed unexpectedly',
        userMessage: 'Could not search job postings. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateJob(jobId: string, updateData: UpdateJobData): Promise<void> {
    try {
      await this.jobUseCase.updateJob(jobId, updateData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.updateJob failed unexpectedly',
        userMessage: 'Could not update the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteJob(jobId: string): Promise<void> {
    try {
      await this.jobUseCase.deleteJob(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.deleteJob failed unexpectedly',
        userMessage: 'Could not delete the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  // ===== Application management =====

  async applyToJob(applicationData: CreateJobApplicationData): Promise<string> {
    try {
      return await this.jobUseCase.applyToJob(applicationData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.applyToJob failed unexpectedly',
        userMessage: 'Could not submit your application. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobApplications(jobId: string): Promise<GetJobApplicationData[]> {
    try {
      return await this.jobUseCase.getJobApplications(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.getJobApplications failed unexpectedly',
        userMessage: 'Could not load job applications. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateApplicationStatus(
    applicationId: string,
    status: JobApplicationStatus,
    employerNotes?: string
  ): Promise<void> {
    try {
      await this.jobUseCase.updateApplicationStatus(applicationId, status, employerNotes);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.updateApplicationStatus failed unexpectedly',
        userMessage: 'Could not update the application status. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getUserApplications(userId: string): Promise<GetJobApplicationData[]> {
    try {
      return await this.jobUseCase.getUserApplications(userId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.getUserApplications failed unexpectedly',
        userMessage: 'Could not load your applications. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  // ===== Statistics and metrics =====

  async getJobStatistics(creatorId: string): Promise<JobStatistics> {
    try {
      return await this.jobUseCase.getJobStatistics(creatorId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.getJobStatistics failed unexpectedly',
        userMessage: 'Could not load job statistics. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async incrementViewCount(jobId: string): Promise<void> {
    try {
      await this.jobUseCase.incrementViewCount(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SERVICE_ERROR',
        devMessage: 'JobsService.incrementViewCount failed unexpectedly',
        userMessage: 'Could not update view count.',
        cause: error,
        level: 'warning',
      });
    }
  }
}
